import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { Parser } from "json2csv";
import { stringify } from "query-string";
import React from "react";
import {
  AutocompleteInput,
  ReferenceInput,
  showNotification,
  Title,
  usePermissions,
} from "react-admin";
import { Form } from "react-final-form";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { apiUrl, getUser } from "../dataProvider";
import {
  renderSelectedSummary,
  SearchableMultiSelect,
} from "../components/SearchableMultiSelect";
import "./report.scss";

interface ReportType {
  id: string;
  name: string;
  hideDate?: boolean;
}

const ISSUE_STATUS_OPTIONS = [
  { id: "open", name: "Open" },
  { id: "resolved", name: "Resolved" },
];

const issueStatusLabel = (id: string) =>
  ISSUE_STATUS_OPTIONS.find((status) => status.id === id)?.name ?? id;

// selecting every status manually is the same as selecting none
const isAllStatuses = (values: string[]) =>
  values.length === ISSUE_STATUS_OPTIONS.length &&
  ISSUE_STATUS_OPTIONS.every((status) => values.includes(status.id));

const Report = (props: any) => {
  const [fromDate, setFromDate] = React.useState(formatDate());
  const [toDate, setToDate] = React.useState(formatDate());
  const [disabled, setDisabled] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<any[]>();
  const { permissions } = usePermissions();
  const [reportTypes, setReportTypes] = React.useState<any[]>([]);
  const [selectedReportType, setSelectedReportType] =
    React.useState<ReportType | null>();
  const [reportTitle, setReportTitle] = React.useState<string>();
  const [hideDate, setHideDate] = React.useState(true);
  const [productSetId, setProductSetId] = React.useState<string>("");
  const [businessUnitId, setBusinessUnitId] = React.useState<string>("");
  const [bookingDateStart, setBookingDateStart] = React.useState("");
  const [bookingDateEnd, setBookingDateEnd] = React.useState("");
  const [hospitalId, setHospitalId] = React.useState<string>("");
  // the issues report filters by status instead of a date range
  const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>([]);
  // distinguishes "Select All" (every status) from a manual selection
  const [selectAllStatuses, setSelectAllStatuses] = React.useState(true);
  const [issueSort, setIssueSort] = React.useState("desc");
  const isIssues = selectedReportType?.id === "issues";

  React.useEffect(() => {
    if (!permissions) {
      return setReportTypes([]);
    }

    let types = [];
    types.push({ id: "bookings", name: "Bookings (All)" });
    types.push({ id: "issues", name: "Issues" });
    types.push({ id: "utilisation", name: "Set Utilisation" });
    types.push({
      id: "product_instance_locations",
      name: "Set Current Locations",
      hideDate: true,
    });
    types.push({ id: "deliveries", name: "Deliveries" });
    types.push({ id: "consignment", name: "Consignment List" });
    if (process.env.REACT_APP_COMPANY_CODE === "S&N") {
      types.push({ id: "surgery_date", name: "Surgery Date" });
    }
    types.push({ id: "amendments", name: "Amendment" });
    types.push({ id: "cancellations", name: "Cancellation" });

    setReportTypes(types);
  }, [permissions]);

  // hide/show date filter depending on the report
  React.useEffect(() => {
    if (!selectedReportType) {
      setHideDate(true);
      return;
    }

    setHideDate(
      (selectedReportType.hideDate ?? false) || selectedReportType.id === "issues",
    );
  }, [selectedReportType]);

  // disable/enable submit button
  React.useEffect(() => {
    setDisabled(!selectedReportType || loading);
  }, [selectedReportType, loading]);

  const handleDownload = async () => {
    //direct download the csv
    let rawData = data ?? [];
    let parser = new Parser();

    if (rawData.length <= 0) {
      props.showNotification(
        selectedReportType?.id === "issues"
          ? "There is no data for the selected statuses"
          : `There is no data from ${fromDate} to ${toDate}`,
        "error",
      );
      return;
    }

    // solution from stackoverflow.com/a/23922475/1043839
    // so, convert the CSV data to blob instead of encodeURI
    const csv = parser.parse(rawData);
    var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    var encodedUri = URL.createObjectURL(blob);

    const fileName =
      selectedReportType?.id === "issues"
        ? selectAllStatuses || isAllStatuses(selectedStatuses)
          ? "issues_all.csv"
          : `issues_${selectedStatuses.join("_") || "none"}.csv`
        : `${selectedReportType?.id}_${fromDate}_${toDate}.csv`;

    var link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);

    link.click();
  };

  function formatDate(date?: string) {
    var d = date ? new Date(date) : new Date(),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  const handleQuery = async () => {
    if (!selectedReportType) return;

    const reportType = selectedReportType.id;
    const foundType = reportTypes.find((type) => type.id === reportType);
    if (!foundType) {
      return;
    }

    setReportTitle(foundType.name);
    setData([]);

    // the backend treats a missing status list as "every status", so a
    // cleared selection must be rejected instead of sent
    if (
      reportType === "issues" &&
      !selectAllStatuses &&
      selectedStatuses.length === 0
    ) {
      props.showNotification("Please select at least one status", "error");
      return;
    }

    setLoading(true);

    const usesStatusFilter = reportType === "issues";
    const allStatusesSelected =
      selectAllStatuses || isAllStatuses(selectedStatuses);
    const filter: Record<string, string | number | string[]> =
      usesStatusFilter
        ? { type: reportType }
        : {
            fromDate: fromDate,
            toDate: toDate,
            type: reportType,
          };

    if (usesStatusFilter) {
      if (!allStatusesSelected) {
        filter.status = selectedStatuses;
      }
      filter.sort = issueSort;
    } else if (reportType === "utilisation") {
      if (hospitalId !== "") {
        filter.hospitalId = Number(hospitalId);
      }
      if (businessUnitId !== "") {
        filter.businessUnitId = Number(businessUnitId);
      }
    } else if (reportType === "surgery_date") {
      if (productSetId !== "") {
        filter.productInstanceId = productSetId;
      }
      if (businessUnitId !== "") {
        filter.businessUnitId = Number(businessUnitId);
      }
      if (bookingDateStart) {
        filter.bookingDateStart = bookingDateStart;
      }
      if (bookingDateEnd) {
        filter.bookingDateEnd = bookingDateEnd;
      }
    } else {
      Object.assign(filter, {
        productInstanceId: productSetId,
        businessUnitId: businessUnitId,
        bookingDateStart,
        bookingDateEnd,
      });
    }

    let query: any = {};
    query["filter"] = JSON.stringify(filter);
    const url = `${apiUrl}/reports?${stringify(query)}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: getUser().user.token,
          Accept: "text/event-stream",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch report");
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("Could not get reader from response body");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        const newItems: any[] = [];
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;
          if (trimmedLine.startsWith("data:")) {
            const dataStr = trimmedLine.replace("data:", "").trim();
            if (dataStr === "[DONE]") break;
            try {
              const parsed = JSON.parse(dataStr);
              if (Array.isArray(parsed)) {
                newItems.push(...parsed);
              } else {
                newItems.push(parsed);
              }
            } catch (e) {
              console.error("Error parsing JSON chunk", e, dataStr);
            }
          }
        }
        if (newItems.length > 0) {
          setData((prev: any) => [...(prev || []), ...newItems]);
        }
      }
    } catch (error) {
      console.error(error);
      props.showNotification("Error fetching report", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderTable = (data: any) => {
    if (loading && (!data || data.length < 1)) {
      return <>Loading...</>;
    }

    if (!data || data.length < 1) {
      return <>No Data</>;
    }

    const keys = Object.keys(data[0]);

    const renderData = (row: any, key: string, index: number) => {
      let value = row[key];

      // Capitalize issue statuses (open -> Open) for readability
      if (key === "Status" && typeof value === "string" && value !== "") {
        value = value.charAt(0).toUpperCase() + value.slice(1);
      }

      // Specifically handle 'invoices' which might be passed as a JSON string
      if (key === "invoices" && typeof value === "string") {
        try {
          value = JSON.parse(value);
        } catch (e) {}
      }

      return (
        <td key={`${index}-${key}`}>
          {Array.isArray(value) ? (
            <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
              {value.map((item: any, i: number) => (
                <li key={i}>
                  {key === "invoices" && typeof item === "object" ? (
                    <span>
                      <b>{item.invoice_number}</b>
                      {item.total_amount &&
                        ` - Invoice Amount: $${item.total_amount}`}
                    </span>
                  ) : (
                    item
                  )}
                </li>
              ))}
            </ul>
          ) : (
            value
          )}
        </td>
      );
    };

    return (
      <div className="table">
        <h2>{reportTitle}</h2>
        <Button
          onClick={handleDownload}
          color="primary"
          size="small"
          disabled={loading}
        >
          Download to CSV
        </Button>
        <table>
          <thead>
            <tr>
              {keys.map((key: string) => (
                <th key={key}>{key}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row: any, index: number) => (
              <tr key={index}>
                {keys.map((key) => renderData(row, key, index))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const handleChange = (event: any) => {
    const nextId = event.target.value;
    const prevId = selectedReportType?.id;
    const foundType = reportTypes.find((type) => type.id === nextId);
    setSelectedReportType(foundType);
    if (prevId === "utilisation" && nextId !== "utilisation") {
      setHospitalId("");
    }
  };

  const handleStatusesChange = (values: string[]) => {
    setSelectAllStatuses(false);
    setSelectedStatuses(values);
  };

  const handleStatusSelectAll = () => {
    // the backend treats a missing status list as "every status", so
    // there is no need to fill the array with every value
    setSelectAllStatuses(true);
    setSelectedStatuses([]);
  };

  const handleStatusClear = () => {
    setSelectAllStatuses(false);
    setSelectedStatuses([]);
  };

  return (
    <Card className="Report">
      {/* @ts-ignore – react-helmet types are incompatible with @types/react >=18 */}
      <Helmet>
        <title>Reports</title>
      </Helmet>
      <Title title="Report" />
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Report
        </Typography>
        <div className="form-report">
          <FormControl variant="filled" className="form-control">
            <InputLabel id="report-type">Report Type</InputLabel>
            <Select
              labelId="report-type"
              id="report-type"
              value={selectedReportType?.id}
              onChange={handleChange}
            >
              <MenuItem value="" key="none">
                <em>None</em>
              </MenuItem>
              {reportTypes
                .sort((a, b) => {
                  if (a.name < b.name) {
                    return -1;
                  }
                  return 1;
                })
                .map((val) => {
                  return (
                    <MenuItem value={val.id} key={val.id}>
                      {val.name}
                    </MenuItem>
                  );
                })}
            </Select>
            {selectedReportType?.id === "utilisation" && (
              <Form
                onSubmit={() => {}}
                record={{}}
                render={() => (
                  <>
                    <ReferenceInput
                      label="Hospital"
                      source="hospital_id"
                      reference="hospitals"
                      allowEmpty
                      fullWidth
                    >
                      <AutocompleteInput
                        optionText="name"
                        variant="filled"
                        fullWidth
                        emptyText="None"
                        onSelect={(record) => setHospitalId(String(record.id))}
                      />
                    </ReferenceInput>
                    <ReferenceInput
                      label="Business Unit"
                      source="business_unit_id"
                      reference="business-units"
                      allowEmpty
                      fullWidth
                    >
                      <AutocompleteInput
                        optionText="name"
                        variant="filled"
                        fullWidth
                        emptyText="None"
                        onSelect={(record) =>
                          setBusinessUnitId(String(record.id))
                        }
                      />
                    </ReferenceInput>
                  </>
                )}
              />
            )}
            {selectedReportType?.id === "surgery_date" && (
              <Form
                onSubmit={() => {}}
                record={{}}
                render={() => (
                  <>
                    <ReferenceInput
                      label="Set"
                      source="product_instance_id"
                      reference="product-instances"
                      allowEmpty
                      fullWidth
                    >
                      <AutocompleteInput
                        optionText="name"
                        variant="filled"
                        fullWidth
                        onSelect={(record) => setProductSetId(record.id)}
                      />
                    </ReferenceInput>
                    <ReferenceInput
                      label="Business Unit"
                      source="business_unit_id"
                      reference="business-units"
                      allowEmpty
                      fullWidth
                    >
                      <AutocompleteInput
                        optionText="name"
                        variant="filled"
                        fullWidth
                        onSelect={(record) =>
                          setBusinessUnitId(String(record.id))
                        }
                      />
                    </ReferenceInput>
                    <TextField
                      className="form-control"
                      variant="filled"
                      id="booking-date-from"
                      label="Surgery From Date"
                      type="date"
                      value={bookingDateStart}
                      onChange={(e) => setBookingDateStart(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      className="form-control"
                      variant="filled"
                      id="booking-date-to"
                      label="Surgery To Date"
                      type="date"
                      value={bookingDateEnd}
                      onChange={(e) => setBookingDateEnd(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </>
                )}
              />
            )}
          </FormControl>
          {isIssues && (
            <div className="status-filter">
              <div className="status-filter-actions">
                <Button size="small" onClick={handleStatusSelectAll}>
                  Select All
                </Button>
                <Button size="small" onClick={handleStatusClear}>
                  Clear
                </Button>
              </div>
              <SearchableMultiSelect
                id="issue-statuses"
                className="form-control"
                label="Status"
                menuWidth={240}
                options={ISSUE_STATUS_OPTIONS}
                value={
                  selectAllStatuses
                    ? ISSUE_STATUS_OPTIONS.map((status) => status.id)
                    : selectedStatuses
                }
                onChange={handleStatusesChange}
                getOptionValue={(status) => status.id}
                renderOption={(status) => ({ primary: status.name })}
                renderValue={(values) => {
                  if (selectAllStatuses || isAllStatuses(values)) {
                    return "All Statuses";
                  }
                  if (values.length === 0) {
                    return "No status selected";
                  }
                  return renderSelectedSummary(values.map(issueStatusLabel));
                }}
                emptyText="No status found"
              />
              <FormControl variant="filled" className="form-control">
                <InputLabel id="issue-sort">Sort</InputLabel>
                <Select
                  labelId="issue-sort"
                  id="issue-sort"
                  value={issueSort}
                  onChange={(e) => setIssueSort(e.target.value as string)}
                >
                  <MenuItem value="desc" key="desc">
                    Newest
                  </MenuItem>
                  <MenuItem value="asc" key="asc">
                    Oldest
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
          )}
          {!hideDate && (
            <>
              <TextField
                className="form-control"
                variant="filled"
                id="date"
                onChange={(e) => setFromDate(e.target.value)}
                defaultValue={fromDate}
                label="From"
                type="date"
              />
              <TextField
                className="form-control"
                variant="filled"
                id="date"
                onChange={(e) => setToDate(e.target.value)}
                defaultValue={toDate}
                label="To"
                type="date"
              />
            </>
          )}
        </div>
        <div className="report-action">
          <Button
            onClick={handleQuery}
            variant="contained"
            disabled={disabled}
            color="primary"
          >
            Search
          </Button>
        </div>

        {renderTable(data)}
      </CardContent>
    </Card>
  );
};

export default connect(null, { showNotification })(Report);
